import {
  Flex,
  Title,
  Image,
  Stack,
  Text,
  useMantineTheme,
  Button,
  Avatar,
  Group,
  UnstyledButton,
  createStyles,
  Space,
  MediaQuery,
  Divider,
  ActionIcon,
  Skeleton,
} from '@mantine/core';
import {AdvancedImage, lazyload, accessibility, responsive, placeholder} from '@cloudinary/react';
import { name as transformationName} from "@cloudinary/url-gen/actions/namedTransformation";
import { IconHeart } from '@tabler/icons';
import { IconHeartFilled } from '@tabler/icons-react';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';
import { useSession, useSupabaseClient } from '@supabase/auth-helpers-react';
import { useClipboard } from '@mantine/hooks';
import Link from 'next/link';
import { useExperiment } from 'statsig-react';
import { TIER_IMAGES_CDN, TIER_IMAGES_TRANSFORM_CDN } from '../../src/Constants';
import { Database } from '../../types/supabase';
import { cld } from '../../src/Utils';

const useStyles = createStyles((theme) => ({
  user: {
    display: 'block',
    height: 50,
    padding: 6,
    flexShrink: 0,
    overflow: 'hidden',
    color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.colors.dark[6],
    backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[3],
    borderRadius: theme.radius.md,

    '&:hover': {
      backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.colors.gray[2],
    },
  },
  tierListShareUser: {
    display: 'block',
    height: 50,
    padding: 6,
    flexShrink: 0,
    overflow: 'hidden',
    color: theme.colors.gray[0],
    background: theme.fn.linearGradient(90, theme.colors.violet[8], theme.colors.pink[8]),
    borderRadius: theme.radius.md,

    '&:hover': {
      backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[8] : theme.colors.gray[2],
    },
  },
}));

export interface TierListHeaderProps {
  tierListData: Database['public']['Tables']['tier_lists']['Row'];
  thumbnail: string;
  isCreator: boolean;
  creatorName: string;
  onShare: (string: string) => void;
  onShareableLink: () => void;
  save_data: Database['public']['Tables']['tier_lists_save']['Row'] | null;
  sharedUsername?: string;
  isSharer: boolean;
  likedPost: boolean;
  clickLikedPost: () => Promise<void>;
}

export default function TierListHeader({
  thumbnail,
  tierListData,
  isCreator,
  creatorName,
  onShareableLink,
  onShare,
  save_data,
  sharedUsername,
  isSharer,
  likedPost,
  clickLikedPost,
}: TierListHeaderProps) {
  const router = useRouter();
  const theme = useMantineTheme();
  const mainViolet = theme.colorScheme === 'dark' ? theme.colors.violet[3] : theme.colors.violet[7];
  const { classes } = useStyles();
  const [viewCount, setViewCount] = useState(1);
  const [likeCount, setLikeCount] = useState(0);
  const clipboard = useClipboard({ timeout: 500 });
  const url = `https://lomolist.com/${tierListData.id}/${tierListData.slug}`;
  const supabase = useSupabaseClient<Database, any>();
  const [childLikedPost, setChildLikedPost] = useState(false);
  const [childLikeCount, setChildLikeCount] = useState(0);
  const [isLoading_React, setIsLoading] = useState(true);
  const isCooking = useRef<boolean>(false);
  const session = useSession();
  const { config, isLoading } = useExperiment('quiz');
  async function loadLikeCount() {
    const { count, error } = await supabase
      .from('template_likes')
      .select('*', { count: 'exact', head: true })
      .eq('template_id', tierListData.id);

    if (count != null) {
      setChildLikeCount(0);
      setLikeCount(count);
    }
  }

  useEffect(() => {
    console.log(config);
  }, [isLoading]);
  useEffect(() => {
    setChildLikedPost(likedPost);
  }, [likedPost]);

  useEffect(() => {
    async function loadViewCount() {
      const { data, error } = await supabase
        .from(save_data ? 'tier_list_views' : 'template_views')
        .select('views')
        .eq('id', save_data ? save_data.save_id : tierListData.id)
        .single();
      setViewCount(data?.views ?? 1);
      if (!save_data) {
        await loadLikeCount();
      }

      setIsLoading(false);
    }
    loadViewCount();
  }, []);

  const image1x = cld.image(`${TIER_IMAGES_CDN}/${thumbnail}`).setDeliveryType('fetch').namedTransformation(transformationName('tier-list-image-2x'))
  const image2x = cld.image(`${TIER_IMAGES_CDN}/${thumbnail}`).setDeliveryType('fetch').namedTransformation(transformationName('tier-list-image-4x'))


  return (
    <>
      <MediaQuery smallerThan="sm" styles={{ display: 'none' }}>
        <Flex>
          <AdvancedImage
            srcset={`${image1x.toURL()} 1x, ${image2x.toURL()} 2x`}
            cldImg={image1x}
            mr="lg"
            width={200}
            height={200}
            fit="contain"
            radius="md"
            style={{
              border: `1px solid ${
                theme.colorScheme === 'dark' ? theme.colors.dark[0] : theme.colors.dark[6]
              }`,
              borderRadius: theme.radius.md,
              overflow: 'hidden',
              marginRight: theme.spacing.lg,
              width: 200,
              height: 200
            }}
            src={`${TIER_IMAGES_CDN}/${thumbnail}`}
            alt={`${tierListData.name} tier list thumbnail`}
          />
          <Stack
            align="flex-start"
            h={200}
            sx={{
              gap: 0,
              overflow: 'hidden',
            }}
          >
            <Text color="dimmed" fw={500}>
              {' '}
              Category {'> '}
              <Link
                href={`/categories/${encodeURIComponent(tierListData.category)}`}
                style={{
                  textDecoration: 'none',
                }}
                passHref
              >
                <Text component="span" td="underline" color="violet.2">
                  {tierListData.category}
                </Text>
              </Link>
            </Text>
            <MediaQuery smallerThan="md" styles={{ display: 'none' }}>
              <Title lineClamp={1} size={30}>
                {' '}
                {tierListData.name}{' '}
                {tierListData.name.toLowerCase().includes('tier list') ? '' : 'Tier List Template'}
              </Title>
            </MediaQuery>
            <MediaQuery largerThan="md" styles={{ display: 'none' }}>
              <Title lineClamp={1} order={3}>
                {' '}
                {tierListData.name}{' '}
                {tierListData.name.toLowerCase().includes('tier list') ? '' : 'Tier List Template'}
              </Title>
            </MediaQuery>
            <Text weight={700} size="sm" lineClamp={4} color="dimmed" maw={600}>
              {tierListData.description}
            </Text>
            {isLoading_React && isLoading ? (
              <Skeleton my="md" h={24} w={120} />
            ) : (
              <Group my="sm" style={{ justifyContent: 'space-between', gap: 7, height: 24 }}>
                <Text size="md" pl="xs" fw="bolder" color="dimmed">
                  Views: {viewCount}
                </Text>

                <UnstyledButton
                  py={2}
                  px={8}
                  style={{
                    justifyContent: 'space-between',
                    gap: 5,
                    backgroundColor:
                      theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[3],
                    borderRadius: 4,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  onClick={async () => {
                    try {
                      if (isCooking.current) {
                        return;
                      }
                      console.log('About to click');
                      await clickLikedPost();
                      if (!session?.user.id) {
                        return;
                      }
                      isCooking.current = true;
                      if (!likedPost) {
                        setChildLikeCount(1);
                      } else {
                        setChildLikeCount(-1);
                      }
                      setChildLikedPost(!childLikedPost);

                      await loadLikeCount();
                    } catch (error) {
                      console.log(error);
                    } finally {
                      isCooking.current = false;
                    }
                  }}
                >
                  {!childLikedPost ? (
                    <IconHeart size={20} />
                  ) : (
                    <IconHeart size={22} fill="red" stroke={0} />
                  )}
                  <Text size="md" fw="bolder" color="dimmed">
                    {Math.max(0, likeCount + childLikeCount)}
                  </Text>
                </UnstyledButton>
              </Group>
            )}

            <Group data-name="profile-button">
              <UnstyledButton
                className={classes.user}
                onClick={() => {
                  router.push(`/profile/${creatorName}`);
                }}
              >
                <Group spacing="xs" pr="xs" sx={{ gap: 2 }}>
                  <Avatar src="" size={35} radius="xl" />

                  <div style={{ flex: 1 }}>
                    <Text size="xs" color="dimmed" weight={700}>
                      Template by:
                    </Text>

                    {creatorName ? (
                      <Text weight={700} size="xs" h={20}>
                        {creatorName}
                      </Text>
                    ) : (
                      <Skeleton h={20} w={120} />
                    )}
                  </div>
                </Group>
              </UnstyledButton>

              {sharedUsername && save_data ? (
                <UnstyledButton
                  className={classes.tierListShareUser}
                  onClick={() => {
                    router.push(`/profile/${sharedUsername}`);
                  }}
                >
                  <Group spacing="xs" sx={{ gap: 2 }}>
                    <Avatar src="" size={35} radius="xl" />

                    <div style={{ flex: 1 }}>
                      <Text size="xs" color="gray.2" weight={700}>
                        Organized by:
                      </Text>

                      <Text weight={700} size="xs" w={120}>
                        {sharedUsername}
                      </Text>
                    </div>
                  </Group>
                </UnstyledButton>
              ) : (
                config.get('show_quiz', false) && (
                  <Link passHref href={`/${tierListData.id}/${tierListData.slug}/quiz`}>
                    <Button variant="white" sx={{ border: '1px solid' }} size="md" my="md">
                      Complete as Quiz
                    </Button>
                  </Link>
                )
              )}

              {/* <Switch value labelPosition="left" label="Image Captions" /> */}
            </Group>
            {/* <Text
            my="xs"
            component="a"
            href="https://google.com"
            td="underline"
            color="dimmed"
            size="xs"
            weight={500}
          >
          Image Credits
        </Text> */}
          </Stack>

          <Space sx={{ flex: 1 }} />
          {!save_data ? (
            <>
              <Divider mx="md" orientation="vertical" size="3" />
              <Flex direction="column" gap={0} mx="lg" h={200}>
                {isCreator ? (
                  <>
                    <Text size="xs" c="dimmed">
                      Want to make changes?
                    </Text>
                    <Button
                      size="xs"
                      variant="outline"
                      onClick={async () => {
                        router.push({
                          pathname: '/create',
                          query: {
                            edit: tierListData.id,
                          },
                        });
                      }}
                    >
                      Edit your list
                    </Button>
                  </>
                ) : (
                  <></>
                )}
                <Space sx={{ flex: 1 }} />
                {/* <Text size="sm" c="dimmed" fw={700}>
            Share your List!
        </Text> */}
                <Stack sx={{ gap: 0 }}>
                  <Button
                    size="xs"
                    color={clipboard.copied ? 'teal' : 'mainViolet'}
                    onClick={() => {
                      onShare('link');
                    }}
                  >
                    Share Link
                  </Button>
                  <Button
                    variant="gradient"
                    my="xs"
                    size="xs"
                    onClick={() => {
                      onShare('twitter');
                    }}
                  >
                    Share to Twitter
                  </Button>
                  <Button
                    size="xs"
                    variant="outline"
                    onClick={() => {
                      onShare('image');
                    }}
                  >
                    Download Image
                  </Button>
                </Stack>
              </Flex>
            </>
          ) : null}
        </Flex>
      </MediaQuery>
      <MediaQuery largerThan="sm" styles={{ display: 'none' }}>
        <Stack>
          <Stack
            align="flex-start"
            sx={{
              gap: 0,
              overflow: 'hidden',
            }}
          >
            <Text color="dimmed" fw={500}>
              {' '}
              Category {'> '}
              <Link
                href={`/categories/${encodeURIComponent(tierListData.category)}`}
                style={{ textDecoration: 'none' }}
                passHref
              >
                <Text component="span" td="underline" color="violet.2">
                  {tierListData.category}
                </Text>
              </Link>
            </Text>
            <Title my="xs" order={2}>
              {' '}
              {tierListData.name}{' '}
              {tierListData.name.toLowerCase().includes('tier list') ? '' : 'Tier List Template'}
            </Title>
            <Text weight={700} size="sm" lineClamp={4} color="dimmed">
              {tierListData.description}
            </Text>
            <Space
              h={2}
              sx={{
                flex: 1,
              }}
            />
            {isLoading_React && isLoading ? (
              <Skeleton my="xs" h={24} w={120} />
            ) : (
              <Group my="xs" style={{ justifyContent: 'space-between', gap: 7, height: 24 }}>
                <Text size="md" fw="bolder" color="dimmed">
                  Views: {viewCount}
                </Text>

                <UnstyledButton
                  py={2}
                  px={8}
                  style={{
                    justifyContent: 'space-between',
                    gap: 5,
                    backgroundColor:
                      theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[3],
                    borderRadius: 4,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                  onClick={async () => {
                    try {
                      if (isCooking.current) {
                        return;
                      }
                      await clickLikedPost();
                      if (!session?.user.id) {
                        return;
                      }
                      isCooking.current = true;
                      if (!likedPost) {
                        setChildLikeCount(1);
                      } else {
                        setChildLikeCount(-1);
                      }
                      setChildLikedPost(!childLikedPost);

                      await loadLikeCount();
                    } catch (error) {
                      console.log(error);
                    } finally {
                      isCooking.current = false;
                    }
                  }}
                >
                  {!childLikedPost ? (
                    <IconHeart size={20} />
                  ) : (
                    <IconHeart size={22} fill="red" stroke={0} />
                  )}
                  <Text size="md" fw="bolder" color="dimmed">
                    {Math.max(0, likeCount + childLikeCount)}
                  </Text>
                </UnstyledButton>
              </Group>
            )}

            <Group my="xs">
              <UnstyledButton
                className={classes.user}
                onClick={() => {
                  router.push(`/profile/${creatorName}`);
                }}
              >
                <Group spacing="xs">
                  <Avatar src="" size={35} radius="xl" />

                  <div style={{ flex: 1 }}>
                    <Text size="xs" color="dimmed" weight={700}>
                      Template by:
                    </Text>
                    {creatorName ? (
                      <Text weight={700} size="xs" w={120} h={20}>
                        {creatorName}
                      </Text>
                    ) : (
                      <Skeleton h={20} w={60} />
                    )}
                  </div>
                </Group>
              </UnstyledButton>
              {sharedUsername && save_data ? (
                <UnstyledButton
                  className={classes.tierListShareUser}
                  onClick={() => {
                    router.push(`/profile/${sharedUsername}`);
                  }}
                >
                  <Group spacing="xs" sx={{ gap: 2 }}>
                    <Avatar src="" size={35} radius="xl" />
                    <div style={{ flex: 1 }}>
                      <Text size="xs" color="gray.2" weight={700}>
                        Organized By:
                      </Text>

                      <Text weight={700} size="xs" w={120}>
                        {sharedUsername}
                      </Text>
                    </div>
                  </Group>
                </UnstyledButton>
              ) : null}
            </Group>
          </Stack>

          <Flex direction="column" gap={0}>
            {isCreator ? (
              <>
                <Text size="sm" c="dimmed" mb="xs">
                  Want to make changes?
                </Text>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={async () => {
                    router.push({
                      pathname: '/create',
                      query: {
                        edit: tierListData.id,
                      },
                    });
                  }}
                >
                  Edit your list
                </Button>
              </>
            ) : (
              <></>
            )}
            <Space h="xs" />
            {!save_data && (
              <Stack sx={{ gap: 0 }}>
                {config.get('show_quiz', false) && (
                  <Link passHref href={`/${tierListData.id}/${tierListData.slug}/quiz`}>
                    <Button variant="white" size="md" my="md" sx={{ width: '100%' }}>
                      Complete as Quiz
                    </Button>
                  </Link>
                )}
                <Button
                  size="sm"
                  color={clipboard.copied ? 'teal' : 'mainViolet'}
                  onClick={() => {
                    onShare('link');
                  }}
                >
                  Share Link
                </Button>
                <Button
                  variant="gradient"
                  my={8}
                  size="sm"
                  onClick={() => {
                    onShare('twitter');
                  }}
                >
                  Share to Twitter
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    onShare('image');
                  }}
                >
                  Download Image
                </Button>
              </Stack>
            )}
          </Flex>
        </Stack>
      </MediaQuery>
    </>
  );
}
